'use client';

import { Card, Heading, Icon, Tabs } from '@mv/web-ui';
import { useI18n } from '@/modules/i18n/useI18n';
import { assertPayloadType } from '@/modules/utils/assert';
import MetrolineRichText from '@/payload/components/Metroline/MetrolineRichText';
import type { RichTextOptions } from '@/payload/components/RichText/RichText';
import type { Brand, Illustration, MobileTutorial, OsVersion } from '@/payload/types';

type MobileTutorialCardBlock = NonNullable<MobileTutorial['cards']>[number];
const metrolineOptions: RichTextOptions = { size: 'small' };

export default function MobileTutorialCard({ icon, cardTitle, targets }: MobileTutorialCardBlock) {
	const { terms } = useI18n();

	function getTabNameFromTarget(
		target: { relationTo: 'brands'; value: number | Brand } | { relationTo: 'os-versions'; value: number | OsVersion },
		label?: string | null,
	) {
		if (label) return label;
		assertPayloadType<Brand | OsVersion>(target.value);

		if (target.relationTo === 'brands') return target.value.name;
		const genericOs = terms.osVersion[`${target.value.osSystem}_generic`];
		if (target.value.osVersion === 'Generic') return genericOs;
		return `${genericOs} ${target.value.osVersion}`;
	}

	return (
		<Card>
			{icon && <CardIllustration illustration={icon} />}
			<Card.Content>
				{cardTitle && (
					<Card.Header>
						<Heading tag="h3" styleAs="h4">
							{cardTitle}
						</Heading>
					</Card.Header>
				)}
				{targets && (
					<Card.Body>
						{targets.length === 1 ? (
							<MetrolineRichText steps={targets[0]?.steps} options={metrolineOptions} />
						) : (
							<Tabs.Root defaultValue={targets[0]?.id || undefined}>
								<Tabs.List>
									{targets.map((target) => (
										<Tabs.Trigger key={target.id} value={target.id || ''}>
											<span>{getTabNameFromTarget(target.target, target.label)}</span>
										</Tabs.Trigger>
									))}
								</Tabs.List>
								{targets.map((target) => (
									<Tabs.Content key={target.id} value={target.id || ''}>
										<MetrolineRichText steps={target.steps} options={metrolineOptions} />
									</Tabs.Content>
								))}
							</Tabs.Root>
						)}
					</Card.Body>
				)}
			</Card.Content>
		</Card>
	);
}

function CardIllustration({ illustration }: { illustration: Illustration | number }) {
	assertPayloadType<Illustration>(illustration);

	return (
		<Card.Icon>
			<Icon iconUrl={illustration.url} size="400" />
		</Card.Icon>
	);
}
